import Alpine from "alpinejs";
import type * as CC from "vanilla-cookieconsent";

// ── Backend-Konfiguration ───────────────────────────────────
// Wird in layouts/app.blade.php im <head> gesetzt, steht hier also bereits
// bereit. Ganz oben gelesen, weil die Cookie-Tabelle sie braucht.
interface BackendConfig {
    MATOMO_URL?: string;
    MATOMO_SITE_ID?: string;
    CONSENT_ENABLED?: boolean;
    CONSENT_HIDE_FROM_BOTS?: boolean;
    CONSENT_AUTOSHOW?: boolean;
    SESSION_COOKIE?: string;
}

const backendConfig = window.__backend_config as BackendConfig | undefined;

// ── Alpine Magic Properties ─────────────────────────────────
interface AlpineMagics {
    $watch(property: string, callback: (value: never) => void): void;
    $nextTick(callback: () => void): void;
    $refs: Record<string, HTMLElement>;
}

// ── CSRF Token ──────────────────────────────────────────────
function getCsrfToken(): string {
    return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "";
}

// ── Matomo ───────────────────────────────────────────────────
let matomoInitialized = false;

function matomoPush(...args: Array<string | number>): void {
    window._paq = window._paq || [];
    window._paq.push(args);
}

// Mit Consent-Manager entscheidet die Kategorie „analytics“, ob Matomo Cookies
// setzen darf: `requireCookieConsent` hält alles zurück, bis eine Einwilligung
// vorliegt. Ohne Consent-Manager gibt es keine Einwilligung einzuholen — dann
// läuft Matomo dauerhaft cookieless (`disableCookies`), so wie es die
// Datenschutzerklärung beschreibt.
function initMatomo(url: string, siteId: string, consentEnabled: boolean): void {
    if (matomoInitialized) {
        return;
    }

    const trackerUrl = url.endsWith("/") ? url : `${url}/`;

    window._paq = window._paq || [];
    matomoPush(consentEnabled ? "requireCookieConsent" : "disableCookies");
    matomoPush("setTrackerUrl", `${trackerUrl}matomo.php`);
    matomoPush("setSiteId", siteId);
    matomoPush("trackPageView");
    matomoPush("enableLinkTracking");

    const script = document.createElement("script");
    script.async = true;
    script.src = `${trackerUrl}matomo.js`;
    document.head.appendChild(script);

    matomoInitialized = true;
}

function enableMatomoCookies(): void {
    matomoPush("setCookieConsentGiven");
}

function disableMatomoCookies(): void {
    matomoPush("forgetCookieConsentGiven");
}

function trackEvent(category: string, action: string, name?: string, value?: number): void {
    if (!matomoInitialized) {
        return;
    }

    const args: Array<string | number> = ["trackEvent", category, action];
    if (name !== undefined) {
        args.push(name);
    }
    if (value !== undefined) {
        args.push(value);
    }

    window._paq = window._paq || [];
    window._paq.push(args);
}

// ── Cookie Data ─────────────────────────────────────────────
const CAT_NECESSARY = "necessary";
const CAT_ANALYTICS = "analytics";
const CAT_ADVERTISEMENT = "advertisement";

interface CookieEntry {
    name: string;
    provider: string;
    description: string;
    expiration: string;
}

interface CookieCategoryDef {
    key: string;
    title: string;
    description: string;
    cookies: CookieEntry[];
}

const cookieCategories: CookieCategoryDef[] = [
    {
        key: "necessary",
        title: "Technisch notwendig",
        description: "Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.",
        cookies: [
            { name: "cc_cookie", provider: "ausmal.club", description: "Speichert die Cookie-Einwilligungen des Nutzers", expiration: "182 Tage" },
            { name: "XSRF-TOKEN", provider: "ausmal.club", description: "Schutz vor Cross-Site-Request-Forgery-Angriffen", expiration: "Sitzung" },
            {
                name: backendConfig?.SESSION_COOKIE ?? "laravel-session",
                provider: "ausmal.club",
                description: "Technische Sitzungsverwaltung",
                expiration: "Sitzung",
            },
        ],
    },
    {
        key: "analytics",
        title: "Analyse & Statistiken",
        description: "Mit deiner Zustimmung nutzt Matomo Cookies für genauere Besucherstatistiken. Ohne Zustimmung funktioniert die Analyse cookieless weiter.",
        cookies: [
            { name: "_pk_id.*", provider: "Matomo (self-hosted)", description: "Besucher-ID für anonyme Statistiken", expiration: "13 Monate" },
            { name: "_pk_ses.*", provider: "Matomo (self-hosted)", description: "Temporäre Sitzungsdaten", expiration: "30 Minuten" },
        ],
    },
    {
        key: "advertisement",
        title: "Werbung",
        description: "Google AdSense verwendet Cookies, um relevante Werbung anzuzeigen.",
        cookies: [
            { name: "_gc_au", provider: "Google AdSense", description: "Speichert Werbe-Klick-Informationen", expiration: "3 Monate" },
            { name: "IDE", provider: "Google DoubleClick", description: "Personalisierte Werbung und Remarketing", expiration: "1 Jahr" },
            { name: "DSID", provider: "Google DoubleClick", description: "Werbe-Einstellungen des Nutzers", expiration: "2 Wochen" },
            { name: "__gads", provider: "Google AdSense", description: "Messung von Werbe-Interaktionen", expiration: "13 Monate" },
            { name: "__gpi", provider: "Google AdSense", description: "Werbe-Personalisierung", expiration: "13 Monate" },
        ],
    },
];

function getCookieTable(categoryKey: string): { headers: Record<string, string>; body: Record<string, string>[] } {
    const category = cookieCategories.find((c) => c.key === categoryKey);
    if (!category) {
        return { headers: {}, body: [] };
    }

    return {
        headers: { name: "Name", domain: "Dienst", description: "Beschreibung", expiration: "Laufzeit" },
        body: category.cookies.map((cookie) => ({
            name: cookie.name,
            domain: cookie.provider,
            description: cookie.description,
            expiration: cookie.expiration,
        })),
    };
}

// ── Cookie Consent ──────────────────────────────────────────
// Die Bibliothek wird erst geladen, wenn der Consent-Manager eingeschaltet ist
// (`CONSENT_ENABLED`). Bis dahin bleibt sie als eigener Chunk außerhalb des
// Haupt-Bundles, und `cookieConsent` ist null.
let cookieConsent: typeof CC | null = null;

function updateConsent(cc: typeof CC): void {
    if (cc.acceptedCategory(CAT_ANALYTICS)) {
        enableMatomoCookies();
    } else {
        disableMatomoCookies();
    }

    if (typeof window.gtag !== "function") {
        return;
    }

    window.gtag("consent", "update", {
        ad_storage: cc.acceptedCategory(CAT_ADVERTISEMENT) ? "granted" : "denied",
        ad_user_data: cc.acceptedCategory(CAT_ADVERTISEMENT) ? "granted" : "denied",
        ad_personalization: cc.acceptedCategory(CAT_ADVERTISEMENT) ? "granted" : "denied",
        analytics_storage: cc.acceptedCategory(CAT_ANALYTICS) ? "granted" : "denied",
    });
}

// `autoShow: false` unterdrückt nur das automatische Einblenden des Banners.
// Die Einwilligungen bleiben gespeichert und `showPreferences()` öffnet den
// Dialog weiterhin – gebraucht auf den App-Seiten, die ausschließlich technisch
// notwendige Cookies setzen (Matomo cookieless, AdSense wird dort nicht
// geladen) und deshalb kein Banner brauchen.
//
// Bibliothek und Stylesheet kommen per dynamischem Import, damit Vite sie in
// einen eigenen Chunk legt: Ohne eingeschalteten Consent-Manager lädt der
// Besucher sie gar nicht erst.
async function initCookieConsent(hideFromBots: boolean, autoShow: boolean): Promise<void> {
    const [cc] = await Promise.all([import("vanilla-cookieconsent"), import("vanilla-cookieconsent/dist/cookieconsent.css")]);

    cookieConsent = cc;

    cc.run({
        disablePageInteraction: true,
        hideFromBots,
        autoShow,

        guiOptions: {
            consentModal: {
                layout: "box wide",
                position: "middle center",
                equalWeightButtons: true,
                flipButtons: false,
            },
            preferencesModal: {
                layout: "box",
                equalWeightButtons: true,
                flipButtons: false,
            },
        },

        onFirstConsent: () => updateConsent(cc),
        onConsent: () => updateConsent(cc),
        onChange: () => updateConsent(cc),

        categories: {
            [CAT_NECESSARY]: { enabled: true, readOnly: true },
            [CAT_ANALYTICS]: {
                autoClear: { cookies: [{ name: /^_pk/ }, { name: /^mtm_/ }] },
            },
            [CAT_ADVERTISEMENT]: {
                autoClear: { cookies: [{ name: /^_gc/ }, { name: "IDE" }, { name: "DSID" }, { name: /^__gads/ }, { name: /^__gpi/ }] },
            },
        },

        language: {
            default: "de",
            translations: {
                de: {
                    consentModal: {
                        title: "Wir verwenden Cookies",
                        description:
                            "Wir nutzen Cookies und ähnliche Technologien, um dir die bestmögliche Erfahrung auf unserer Website zu bieten. Einige sind technisch notwendig, andere helfen uns, die Website zu verbessern und personalisierte Werbung anzuzeigen.",
                        acceptAllBtn: "Alle akzeptieren",
                        acceptNecessaryBtn: "Alle ablehnen",
                        showPreferencesBtn: "Einstellungen verwalten",
                        footer: '<a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a>',
                    },
                    preferencesModal: {
                        title: "Cookie-Einstellungen",
                        acceptAllBtn: "Alle akzeptieren",
                        acceptNecessaryBtn: "Alle ablehnen",
                        savePreferencesBtn: "Auswahl speichern",
                        closeIconLabel: "Schließen",
                        sections: [
                            {
                                title: "Deine Privatsphäre",
                                description:
                                    "Hier kannst du deine Cookie-Einstellungen anpassen. Du kannst deine Auswahl jederzeit ändern, indem du unten im Footer auf \u201ECookie-Einstellungen\u201C klickst.",
                            },
                            {
                                title: "Technisch notwendig",
                                description: cookieCategories.find((c) => c.key === CAT_NECESSARY)!.description,
                                linkedCategory: CAT_NECESSARY,
                                cookieTable: getCookieTable(CAT_NECESSARY),
                            },
                            {
                                title: "Analyse & Statistiken",
                                description: cookieCategories.find((c) => c.key === CAT_ANALYTICS)!.description,
                                linkedCategory: CAT_ANALYTICS,
                                cookieTable: getCookieTable(CAT_ANALYTICS),
                            },
                            {
                                title: "Werbung",
                                description:
                                    cookieCategories.find((c) => c.key === CAT_ADVERTISEMENT)!.description +
                                    ' Du kannst deine Werbe-Einstellungen auch bei <a href="https://g.co/adsettings" target="_blank" rel="noopener">Google Ads</a> anpassen.',
                                linkedCategory: CAT_ADVERTISEMENT,
                                cookieTable: getCookieTable(CAT_ADVERTISEMENT),
                            },
                            {
                                title: "Weitere Informationen",
                                description:
                                    'Bei Fragen zu unseren Cookies und deinen Einstellungen kontaktiere uns gerne per <a href="mailto:kontakt@ausmal.club">E-Mail</a>. Weitere Details findest du in unserer <a href="/datenschutz">Datenschutzerklärung</a>.',
                            },
                        ],
                    },
                },
            },
        },
    });
}

// ── Sentry ──────────────────────────────────────────────────
function initSentry(): void {
    const dsn = window.__SENTRY_DSN;
    if (!dsn) {
        return;
    }

    import("@sentry/browser").then((Sentry) => {
        Sentry.init({ dsn });
    });
}

// ── Alpine Components ───────────────────────────────────────
function mobileMenu() {
    return {
        open: false,
        toggle() {
            this.open = !this.open;
        },
        close() {
            this.open = false;
        },
    };
}

function searchOverlay() {
    return {
        open: false,
        query: "",
        suggestions: [] as Array<{
            type: string;
            title: string;
            slug: string;
            categorySlug: string | null;
            parentCategorySlug: string | null;
            thumbnailUrl: string | null;
        }>,
        selectedIndex: -1,
        loading: false,
        debounceTimer: null as ReturnType<typeof setTimeout> | null,

        init(
            this: AlpineMagics & {
                selectedIndex: number;
                debounceTimer: ReturnType<typeof setTimeout> | null;
                suggestions: unknown[];
                fetchSuggestions: (q: string) => void;
            },
        ) {
            this.$watch("query", (newQuery: string) => {
                this.selectedIndex = -1;
                if (this.debounceTimer) {
                    clearTimeout(this.debounceTimer);
                }
                if (newQuery.length < 1) {
                    this.suggestions = [];
                    return;
                }
                this.debounceTimer = setTimeout(() => {
                    this.fetchSuggestions(newQuery);
                }, 300);
            });
        },

        openSearch(this: AlpineMagics & { open: boolean; query: string; suggestions: unknown[]; selectedIndex: number }) {
            this.open = true;
            this.query = "";
            this.suggestions = [];
            this.selectedIndex = -1;
            this.$nextTick(() => {
                (this.$refs.searchInput as HTMLInputElement)?.focus();
            });
        },

        closeSearch() {
            this.open = false;
            this.query = "";
            this.suggestions = [];
            this.selectedIndex = -1;
        },

        async fetchSuggestions(q: string) {
            this.loading = true;
            try {
                const response = await fetch(`/api/search/autocomplete?q=${encodeURIComponent(q)}`);
                if (response.ok) {
                    const data = await response.json();
                    this.suggestions = data.data ?? data;
                }
            } catch {
                this.suggestions = [];
            } finally {
                this.loading = false;
            }
        },

        moveSelection(direction: number) {
            const maxIndex = this.suggestions.length;
            this.selectedIndex = Math.max(-1, Math.min(maxIndex, this.selectedIndex + direction));
        },

        handleEnter() {
            if (this.selectedIndex >= 0 && this.selectedIndex < this.suggestions.length) {
                this.navigateToSuggestion(this.suggestions[this.selectedIndex]);
            } else {
                this.navigateToResults();
            }
        },

        navigateToSuggestion(suggestion: { type: string; slug: string; categorySlug: string | null; parentCategorySlug: string | null; title: string }) {
            this.closeSearch();
            let url: string;

            if (suggestion.type === "category") {
                url = suggestion.parentCategorySlug ? `/ausmalbilder/${suggestion.parentCategorySlug}/${suggestion.slug}` : `/ausmalbilder/${suggestion.slug}`;
            } else {
                url = `/ausmalbilder/${suggestion.parentCategorySlug ?? ""}/${suggestion.categorySlug ?? ""}/${suggestion.slug}`;
            }

            trackEvent("Search", "Autocomplete", suggestion.title);
            window.location.href = url;
        },

        navigateToResults() {
            if (this.query.length < 1) {
                return;
            }
            const query = this.query;
            this.closeSearch();
            trackEvent("Search", "Query", query);
            window.location.href = `/suche?q=${encodeURIComponent(query)}`;
        },
    };
}

function newsletterForm() {
    return {
        email: "",
        name: "",
        isSubscribing: false,
        message: "",
        success: false,

        async submit() {
            if (!this.email) {
                return;
            }

            this.isSubscribing = true;
            this.message = "";
            this.success = false;

            try {
                const response = await fetch("/api/newsletter/subscribe", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        "X-CSRF-TOKEN": getCsrfToken(),
                    },
                    body: JSON.stringify({
                        email: this.email,
                        name: this.name || null,
                    }),
                });

                const data = await response.json();

                if (response.ok && data.success) {
                    this.message = data.message;
                    this.success = true;
                    this.email = "";
                    this.name = "";
                    trackEvent("Newsletter", "Subscribe", "Homepage");
                } else {
                    this.message = data.message || "Bei der Anmeldung ist ein Fehler aufgetreten.";
                    this.success = false;
                }
            } catch {
                this.message = "Ein Fehler ist aufgetreten. Bitte versuche es später erneut.";
                this.success = false;
            } finally {
                this.isSubscribing = false;
            }
        },
    };
}

function downloadModal(categorySlug: string, imageId: number) {
    return {
        open: false,
        loading: false,
        downloadUrl: null as string | null,
        error: null as string | null,

        openModal() {
            this.open = true;
            this.downloadUrl = null;
            this.error = null;
            document.body.style.overflow = "hidden";
            this.fetchDownloadUrl();
        },

        closeModal() {
            this.open = false;
            this.downloadUrl = null;
            this.error = null;
            document.body.style.overflow = "";
        },

        async fetchDownloadUrl() {
            this.loading = true;
            this.error = null;

            try {
                const response = await fetch(`/api/images/${categorySlug}/${imageId}/download-url`, {
                    method: "POST",
                    headers: {
                        Accept: "application/json",
                        "X-CSRF-TOKEN": getCsrfToken(),
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    this.downloadUrl = data.downloadUrl ?? null;
                } else {
                    this.error = "Download-Link konnte nicht erstellt werden. Bitte versuche es erneut.";
                }
            } catch {
                this.error = "Download-Link konnte nicht erstellt werden. Bitte versuche es erneut.";
            } finally {
                this.loading = false;
            }
        },

        trackDownload() {
            trackEvent("PDF", "Download", `${categorySlug}/${imageId}`);
        },
    };
}

function sudokuDownloadModal(pdfUrl: string, label: string) {
    return {
        open: false,
        pdfUrl,

        openModal() {
            this.open = true;
            document.body.style.overflow = "hidden";
        },

        closeModal() {
            this.open = false;
            document.body.style.overflow = "";
        },

        trackDownload() {
            trackEvent("PDF", "Download", `sudoku/${label}`);
        },
    };
}

// ── App-Promo im Sticky-Slot ────────────────────────────────
// Unten am Handy-Bildschirm steht entweder der Hinweis auf unsere App oder die
// Anzeige, nie beides. Wer den Hinweis wegklickt, sieht dort sieben Tage lang
// wieder die Anzeige.
//
// Der Merker liegt im localStorage und nicht hinter der Einwilligung: ein
// einziger Wert, rein technisch, ohne Personenbezug – er setzt genau die
// Entscheidung um, die der Besucher gerade getroffen hat (§25 Abs. 2 TDDDG).
const APP_PROMO_DISMISS_KEY = "ausmal.app-promo.dismissed-until";
const APP_PROMO_DISMISS_DAYS = 7;

/** Ausgangswert `hidden`: bis `init()` entschieden hat, blendet x-cloak beide Fassungen aus. */
type AppPromoStickyMode = "hidden" | "promo" | "ad";

function appPromoDismissalIsActive(): boolean {
    try {
        const until = window.localStorage.getItem(APP_PROMO_DISMISS_KEY);

        return until !== null && Date.parse(until) > Date.now();
    } catch {
        // Privater Modus oder gesperrter Speicher: dann gilt der Hinweis als nicht weggeklickt.
        return false;
    }
}

function rememberAppPromoDismissal(): void {
    try {
        const until = new Date(Date.now() + APP_PROMO_DISMISS_DAYS * 24 * 60 * 60 * 1000);
        window.localStorage.setItem(APP_PROMO_DISMISS_KEY, until.toISOString());
    } catch {
        // Lässt sich nichts speichern, gilt das Wegklicken eben nur für diesen Seitenaufruf.
    }
}

function appPromoSticky({ hasPromo = false, hasPlayStore = false }: { hasPromo?: boolean; hasPlayStore?: boolean } = {}) {
    return {
        mode: "hidden" as AppPromoStickyMode,

        /** Schaltet in der Leiste zwischen „Im App Store“ und „Bei Google Play“ um. */
        isAndroid: false,

        init() {
            this.isAndroid = /Android/i.test(navigator.userAgent);

            // Auf Android ohne Play-Link gäbe es nichts zu laden – dort bleibt es bei der Anzeige.
            this.mode = !hasPromo || (this.isAndroid && !hasPlayStore) || appPromoDismissalIsActive() ? "ad" : "promo";
        },

        dismiss() {
            rememberAppPromoDismissal();
            this.mode = "ad";
            trackEvent("AusmalClubApp", "Dismiss", "sticky");
        },
    };
}

// ── App-Promo im Download-Dialog ────────────────────────────
// Am Handy steht die App-Karte oben und Amazon kompakt darunter – außer auf
// Android ohne Google-Play-Link, dort gäbe es nichts zu laden. Den Breakpoint
// regelt CSS (sm:hidden / max-sm:hidden), hier fällt nur die Plattformfrage.
// Der Dialog ist bis zum ersten Klick unsichtbar, die Entscheidung in init()
// steht also immer fest, bevor er erscheint.
function downloadModalPromos({ hasPromo = false, hasPlayStore = false }: { hasPromo?: boolean; hasPlayStore?: boolean } = {}) {
    return {
        /** Schaltet in der App-Karte zwischen „Im App Store“ und „Bei Google Play“ um. */
        isAndroid: false,

        /** Am Handy: App-Karte oben, Amazon kompakt unter dem Download. */
        appFirst: false,

        init() {
            this.isAndroid = /Android/i.test(navigator.userAgent);
            this.appFirst = hasPromo && (!this.isAndroid || hasPlayStore);
        },
    };
}

// ── Register Alpine Components ──────────────────────────────
window.Alpine = Alpine;

Alpine.data("mobileMenu", mobileMenu);
Alpine.data("searchOverlay", searchOverlay);
Alpine.data("newsletterForm", newsletterForm);
Alpine.data("downloadModal", downloadModal);
Alpine.data("sudokuDownloadModal", sudokuDownloadModal);
Alpine.data("appPromoSticky", appPromoSticky);
Alpine.data("downloadModalPromos", downloadModalPromos);

Alpine.start();

// ── Init Services ───────────────────────────────────────────
const consentEnabled = backendConfig?.CONSENT_ENABLED === true;

if (backendConfig?.MATOMO_URL && backendConfig?.MATOMO_SITE_ID) {
    initMatomo(backendConfig.MATOMO_URL, backendConfig.MATOMO_SITE_ID, consentEnabled);
}

if (consentEnabled) {
    void initCookieConsent(backendConfig?.CONSENT_HIDE_FROM_BOTS !== false, backendConfig?.CONSENT_AUTOSHOW !== false);
}

initSentry();

// ── Uhr-Illustrationen auf die lokale Zeit stellen ───────────
// Im Markup steht die deutsche Zeit vom Server. Weil Seiten gecacht ausgeliefert
// werden und Besucher in anderen Zeitzonen sitzen können, werden die Zeiger nach
// dem Laden einmal auf die Zeit des Besuchers gestellt — ohne Animation,
// ohne Intervall. Der Drehpunkt kommt aus data-clock (SVG-Koordinaten).
function setClockHandsToLocalTime(): void {
    const now = new Date();
    const angles: Record<string, number> = {
        hour: (now.getHours() % 12) * 30 + now.getMinutes() * 0.5,
        minute: now.getMinutes() * 6,
    };

    document.querySelectorAll<SVGSVGElement>("[data-clock]").forEach((clock) => {
        const center = clock.dataset.clock?.trim() || "60 60";

        for (const [hand, angle] of Object.entries(angles)) {
            clock.querySelector<SVGElement>(`[data-hand="${hand}"]`)?.setAttribute("transform", `rotate(${angle.toFixed(1)} ${center})`);
        }
    });
}

// ── Scroll-Reveal ────────────────────────────────────────────
// Abschnitte mit .reveal blenden beim Hereinscrollen ein. Der versteckte
// Ausgangszustand steht im CSS und hängt an .app-reveal — die Klasse setzt
// diese Funktion selbst, und zwar erst, wenn wirklich beobachtet werden kann.
// Ohne JavaScript, ohne IntersectionObserver oder bei reduzierter Bewegung
// fehlt sie, und alles steht sichtbar da.
// Animiert werden nur opacity und transform, das Layout verschiebt sich nie.
const REVEAL_ROOT_CLASS = "app-reveal";
const REVEAL_FALLBACK_DELAY_MS = 2000;

function initScrollReveal(): void {
    const elements = document.querySelectorAll<HTMLElement>(".reveal");
    if (elements.length === 0) {
        return;
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
        elements.forEach((element) => element.classList.add("is-revealed"));
        return;
    }

    document.documentElement.classList.add(REVEAL_ROOT_CLASS);

    const ordered = Array.from(elements);
    let hasRevealed = false;

    // Springt jemand mit einem Ankerlink mitten in die Seite, überspringt der
    // Sprung alles dazwischen — für übersprungene Elemente meldet sich der
    // Observer nie. Deshalb wird mit jedem Treffer auch alles davor
    // aufgedeckt: Was oberhalb eines sichtbaren Elements liegt, ist bereits
    // vorbeigezogen.
    function revealUpTo(index: number): void {
        hasRevealed = true;

        for (let i = 0; i <= index; i++) {
            ordered[i].classList.add("is-revealed");
            observer.unobserve(ordered[i]);
        }
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) {
                    return;
                }
                revealUpTo(ordered.indexOf(entry.target as HTMLElement));
            });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.08 },
    );

    ordered.forEach((element) => observer.observe(element));

    // Sicherheitsnetz: Schlägt der Observer binnen zwei Sekunden kein einziges
    // Mal an (etwa weil eine Seite so kurz ist, dass nichts in den Blick
    // gerät, oder weil eine Erweiterung dazwischenfunkt), wird der versteckte
    // Ausgangszustand wieder aufgehoben. Inhalt darf nie unsichtbar bleiben.
    window.setTimeout(() => {
        if (!hasRevealed) {
            document.documentElement.classList.remove(REVEAL_ROOT_CLASS);
        }
    }, REVEAL_FALLBACK_DELAY_MS);
}

function initOnDomReady(): void {
    setClockHandsToLocalTime();
    initScrollReveal();
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initOnDomReady);
} else {
    initOnDomReady();
}

// ── Global Event Listeners ──────────────────────────────────
// Ohne geladenen Consent-Manager gibt es keinen Dialog zu öffnen — der Footer
// blendet den Knopf dann ohnehin aus.
document.addEventListener("open-cookie-preferences", () => {
    cookieConsent?.showPreferences();
});

// ── Delegiertes Klick-Tracking ──────────────────────────────
// Jedes Element mit data-track-category meldet seinen Klick an Matomo.
// data-track-action ist optional (Default "Click"), data-track-name auch.
// Kein preventDefault: der Link folgt ganz normal seinem href.
document.addEventListener("click", (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) {
        return;
    }

    const trackable = target.closest<HTMLElement>("[data-track-category]");
    if (!trackable) {
        return;
    }

    const category = trackable.dataset.trackCategory;
    if (!category) {
        return;
    }

    trackEvent(category, trackable.dataset.trackAction ?? "Click", trackable.dataset.trackName);
});
